import { createApp, defineComponent, h, ref, type App, type Component, type Ref } from 'vue';

import { uuidV4 } from '@bv/utils/common/uuid-v4';

type EventType = (...args: any[]) => any;
type EventsType = Record<string, EventType>;

/**
 * A generic manager for the lifecycle of any Vue component injected as a widget.
 * It handles initialization, mounting, dynamic property updates, and cleanup.
 * @template Props - The type of the props object for the widget component.
 */
export class WidgetInjector<Props extends Record<string, any> = object, Events extends EventsType = EventsType> {
  /**
   * Tracks if the widget has been initialized to prevent re-initialization.
   * @private
   */
  private isWidgetInitialized = false;
  /**
   * The reactive properties object passed to the Vue widget.
   * Stored on the instance to allow for dynamic updates from other methods.
   * @private
   */
  private widgetProps: Props | null = null;

  /**
   * A map of formatted event handler functions to be passed directly to the Vue component.
   * The keys are event names transformed into the `onEventName` camelCase format
   * required by Vue's `h` function, and the values are the callback functions
   * provided during initialization.
   * @private
   */
  private eventHandlers: Events = {} as Events;

  /**
   * The root Vue application instance.
   * Stored for later unmounting.
   * @private
   */
  private appInstance: App | null = null;

  /**
   * The DOM element that hosts the Vue application.
   * @private
   */
  private containerElement: HTMLElement | null = null;

  /**
   * A reactive ref that acts as a re-render trigger.
   * Changing its value will force the wrapper component to re-render.
   * @private
   */
  private renderKey: Ref<number> = ref(0);

  /**
   * The Vue component to be rendered as a widget.
   * @private
   */
  private readonly component: Component;

  /**
   * The ID for the widget's container element.
   * @private
   */
  private readonly containerId: string;

  /**
   * @param {Component} component - The Vue component to manage.
   * @param {object} [options] - Configuration options.
   * @param {string} [options.containerId=uuidV4()] - The ID for the DOM element that will host the widget.
   *                                                            If is undefined default value = uuid
   */
  constructor(component: Component, options?: { containerId?: string }) {
    this.component = component;
    this.containerId = options?.containerId || uuidV4();
  }

  /**
   * Initializes and mounts the Vue widget into the DOM.
   * @public
   * @param {object} options - Configuration for the widget.
   * @param {Props} [options.props] - Initial props for the widget.
   * @param {Record<string, Function>} [options.events] - A map of event names to callback functions.
   */
  public async initWidget(options: { props?: Props; events?: Events } = {}): Promise<void> {
    if (this.isWidgetInitialized) {
      return;
    }

    this.widgetProps = options.props || ({} as Props);
    this.eventHandlers = this.formatEventHandlers(options.events);

    // Define the wrapper component using the class instance's props
    const WidgetAppWrapper = defineComponent({
      // By accessing this.renderKey.value, we make this component
      // dependent on it. When the key changes, this component re-renders.
      render: () => {
        if (this.widgetProps && this.renderKey.value >= 0) {
          const combinedProps = { ...this.widgetProps, ...this.eventHandlers };
          return h(this.component, combinedProps);
        }
        return null;
      },
    });

    // Create and configure the Vue app
    this.appInstance = createApp(WidgetAppWrapper);

    const { vuetify } = await import('@bv/plugins/vuetify');

    this.appInstance.use(vuetify);

    // Create the container element (Corrected 'div') and store it
    this.containerElement = document.createElement('div');
    this.containerElement.id = this.containerId;
    document.body.prepend(this.containerElement);

    // Mount the app and set the initialized flag
    this.appInstance.mount(this.containerElement);
    this.isWidgetInitialized = true;
  }

  /**
   * Unmounts the Vue application and removes its container from the DOM.
   * Resets the manager's state to allow for re-initialization.
   * @public
   */
  public destroyWidget(): void {
    if (!this.isWidgetInitialized || !this.appInstance || !this.containerElement) {
      return;
    }

    // Unmount the Vue app to trigger cleanup
    this.appInstance.unmount();

    // Remove the container element from the DOM
    this.containerElement.remove();

    // Reset state
    this.isWidgetInitialized = false;
    this.appInstance = null;
    this.containerElement = null;
    this.widgetProps = null;
  }

  /**
   * Dynamically updates one or more properties of the widget.
   * This method merges the provided props with the current reactive state.
   * @public
   * @param {Partial<Props>} newProps - An object containing the properties to update.
   */
  public updateProps(newProps: Partial<Props>): void {
    if (this.widgetProps) {
      // Merge the new properties into the existing reactive object.
      // Vue's reactivity system will automatically detect the changes.
      Object.assign(this.widgetProps, newProps);
    } else {
      throw new Error('Cannot update props: widget is not initialized.');
    }
  }

  /**
   * Merges new event listeners with the existing ones.
   * Use this to add or update specific listeners without removing others.
   * @public
   * @param {Events} newEvents - Listeners to add or update.
   */
  public addEvents(newEvents: Events): void {
    if (!this.isWidgetInitialized) {
      throw new Error('Cannot merge events: widget is not initialized.');
    }

    // Format the new event handlers
    const formattedNewEvents = this.formatEventHandlers(newEvents);

    // Merge the new handlers into the existing ones
    Object.assign(this.eventHandlers, formattedNewEvents);

    // Force the component to re-render to apply the changes
    this.renderKey.value++;
  }

  /**
   * Converts event names from kebab-case to onCamelCase format for Vue's h function.
   * e.g., 'on-expand' becomes 'onOnExpand'.
   * @private
   */
  private formatEventHandlers(handlers: Events = {} as Events): Events {
    const formatted: EventsType = {};
    for (const key in handlers) {
      const camelCaseKey = key.replace(/-(\w)/g, (_, c) => c.toUpperCase());
      const finalKey = `on${camelCaseKey.charAt(0).toUpperCase() + camelCaseKey.slice(1)}`;
      formatted[finalKey] = handlers[key];
    }
    return formatted as Events;
  }
}
